import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Save, RotateCcw, X } from "lucide-react";

const TABS = [["verses", "Verses"], ["content", "Topics & Content"], ["analytics", "Analytics"], ["data", "Import / Export"], ["users", "Users"], ["card-orders", "Card Orders"]];

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

export function Admin() {
  const [tab, setTab] = useState("verses");
  const [resetAt, setResetAt] = useState(null);
  const loadBaseline = () => api.get("/admin/analytics/baseline").then((r) => setResetAt(r.data.reset_at)).catch(() => {});
  useEffect(() => { loadBaseline(); }, []);
  const resetStats = async () => {
    if (!window.confirm("Reset the LIVE dashboard stats to right now?\n\nThis does NOT delete any records — it only changes which date the dashboard starts counting from. Everything before now will be excluded from live totals.")) return;
    try { const r = await api.post("/admin/analytics/reset"); setResetAt(r.data.reset_at); toast.success("Live stats baseline reset to now."); }
    catch { toast.error("Could not reset stats."); }
  };
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-serif-display text-4xl text-[#0F172A] mb-1">Admin Dashboard</h1>
      <p className="text-[#475569] mb-3">Review, edit, and manage the Proverbs database.</p>
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <span data-testid="live-stats-since" className="inline-flex items-center gap-2 rounded-full bg-[#EAF1EA] text-[#2C4C3B] px-3.5 py-1.5 text-xs font-semibold">
          Live Stats Since: {fmtDateTime(resetAt)}
        </span>
        <button data-testid="reset-live-stats" onClick={resetStats}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E2E8F0] text-[#475569] px-3.5 py-1.5 text-xs font-semibold hover:border-[#2C4C3B] hover:text-[#2C4C3B] transition-colors">
          <RotateCcw className="w-3.5 h-3.5" /> Reset live stats
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0]">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-[#2C4C3B] text-[#2C4C3B]" : "border-transparent text-[#94A3B8] hover:text-[#475569]"}`}>{l}</button>
        ))}
      </div>
      {tab === "verses" && <Verses />}
      {tab === "content" && <ContentTools />}
      {tab === "analytics" && <Analytics />}
      {tab === "data" && <DataTools />}
      {tab === "users" && <Users />}
      {tab === "card-orders" && <CardOrders />}
    </div>
  );
}

function StatCard({ label, value, testid, accent }) {
  return (
    <div data-testid={testid} className={`rounded-xl border p-5 ${accent ? "border-[#2C4C3B] bg-[#EAF1EA]" : "border-[#E2E8F0] bg-white"}`}>
      <p className="text-3xl font-semibold text-[#0F172A]">{value}</p>
      <p className="text-xs uppercase tracking-widest text-[#94A3B8] mt-1">{label}</p>
    </div>
  );
}

function FunnelPill({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 flex items-center justify-between gap-3">
      <span className="text-sm text-[#475569]">{label}</span>
      <span className="text-lg font-semibold text-[#0F172A]">{value}</span>
    </div>
  );
}

function CardOrderStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/card-orders/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-[#94A3B8] mb-6">Loading card stats…</p>;
  return (
    <div data-testid="card-order-stats" className="mb-10 space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard testid="stat-orders" label="Paid Orders" value={s.orders} accent />
        <StatCard testid="stat-cards" label="Cards Ordered" value={s.cards_ordered.toLocaleString()} />
        <StatCard testid="stat-revenue" label="Card Revenue" value={s.revenue_display} />
        <StatCard testid="stat-aov" label="Avg Order Value" value={s.aov_display} />
      </div>

      {s.by_quantity.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Orders by quantity</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {s.by_quantity.map((b) => <StatCard key={b.quantity} testid={`stat-qty-${b.quantity}`} label={b.label} value={b.orders} />)}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Order status funnel</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FunnelPill testid="funnel-checkout" label="Checkout Started" value={s.funnel.checkout_started} />
          <FunnelPill testid="funnel-paid" label="Paid" value={s.funnel.paid} />
          <FunnelPill testid="funnel-pending" label="Fulfillment Pending" value={s.funnel.fulfillment_pending} />
          <FunnelPill testid="funnel-submitted" label="Submitted to Printer" value={s.funnel.submitted_to_printer} />
          <FunnelPill testid="funnel-shipped" label="Shipped" value={s.funnel.shipped} />
          <FunnelPill testid="funnel-failed" label="Failed / Abandoned" value={s.funnel.failed_abandoned} />
        </div>
      </div>
    </div>
  );
}

const STATUS_META = {
  awaiting_payment: ["Awaiting payment", "bg-[#FEF3C7] text-[#92400E]"],
  new: ["New", "bg-[#E0E7FF] text-[#3730A3]"],
  in_production: ["In Production", "bg-[#DBEAFE] text-[#1E40AF]"],
  shipped: ["Shipped", "bg-[#DCFCE7] text-[#166534]"],
  delivered: ["Delivered", "bg-[#D1FAE5] text-[#065F46]"],
  cancelled: ["Cancelled", "bg-[#FEE2E2] text-[#991B1B]"],
};
function StatusBadge({ status }) {
  const [label, cls] = STATUS_META[status] || [status || "—", "bg-[#F1F5F9] text-[#475569]"];
  return <span data-testid="status-badge" className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function OrderDetailModal({ id, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/admin/card-orders/${id}`).then((r) => {
      setD(r.data);
      setForm({
        admin_status: r.data.admin_status || "",
        card_type: r.data.card_type || "standard",
        sponsor_name: r.data.sponsor_name || "",
        tracking_code: r.data.tracking_code || "",
        tracking_url: r.data.tracking_url || "",
        admin_notes: r.data.admin_notes || "",
        reprint_refund_status: r.data.reprint_refund_status || "",
        notify_customer: true,
      });
    }).catch(() => toast.error("Could not load order"));
  }, [id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/card-orders/${id}/update`, form);
      if (r.data.email_sent === true) toast.success("Saved. Shipping email sent to customer.");
      else if (form.admin_status === "shipped" && r.data.email_sent === false) toast.success("Saved. (Shipping email not sent — email service not configured.)");
      else toast.success("Order updated.");
      onSaved?.(r.data.order);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };
  const ship = d?.shipping || {};
  return (
    <div data-testid="order-detail-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-serif-display text-2xl text-[#0F172A]">Order details</h3>
          <button data-testid="order-detail-close" onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        {!d || !form ? <p className="text-[#94A3B8]">Loading…</p> : (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: read-only order facts */}
            <dl className="space-y-2.5 text-sm">
              <div className="flex items-center gap-2"><StatusBadge status={d.effective_status} />{d.test_mode && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">TEST</span>}</div>
              <DRow label="Order number" value={d.order_reference_id} />
              <DRow label="Date" value={fmtDateTime(d.created_at)} />
              <DRow label="Customer" value={d.customer_name} />
              <DRow label="Email" value={d.customer_email} />
              <DRow label="Phone" value={d.customer_phone || "—"} />
              <DRow label="Quantity" value={d.quantity} />
              <DRow label="Amount paid" value={d.amount_display} />
              <DRow label="Payment (Stripe)" value={d.payment_status} />
              <DRow label="Gelato order" value={d.gelato_order_id || "—"} />
              <div className="pt-2 border-t border-[#F0EAdd]">
                <dt className="text-[#94A3B8] mb-1">Ship to</dt>
                <dd className="text-[#0F172A]">{[ship.addressLine1, ship.addressLine2, [ship.city, ship.state].filter(Boolean).join(", "), ship.postCode].filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}</dd>
              </div>
              {d.fulfillment_error && <p className="text-[13px] text-[#B45309] pt-2">{d.fulfillment_error}</p>}
              <div className="pt-2 border-t border-[#F0EAdd]">
                <dt className="text-[#94A3B8] mb-2">Card artwork</dt>
                <div className="flex gap-2">
                  <img data-testid="preview-front" src={d.preview_front} alt="front" className="w-24 h-auto rounded border border-[#E2E8F0]" />
                  <img data-testid="preview-back" src={d.preview_back} alt="back" className="w-24 h-auto rounded border border-[#E2E8F0]" />
                </div>
                {d.print_file_url && <a data-testid="print-file-link" href={d.print_file_url} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs text-[#2C4C3B] underline">Open finalized print file (PDF)</a>}
              </div>
            </dl>

            {/* Right: editable fulfillment form */}
            <div className="space-y-3">
              <LabeledField label="Fulfillment status">
                <select data-testid="edit-status" value={form.admin_status} onChange={(e) => set("admin_status", e.target.value)}
                  className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm">
                  <option value="">Auto ({STATUS_META[d.effective_status]?.[0] || d.effective_status})</option>
                  <option value="new">New</option>
                  <option value="in_production">In Production</option>
                  <option value="shipped">Shipped</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </LabeledField>
              <LabeledField label="Card type">
                <select data-testid="edit-card-type" value={form.card_type} onChange={(e) => set("card_type", e.target.value)}
                  className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm">
                  <option value="standard">Standard I Need a Proverb</option>
                  <option value="sponsor_qr">Sponsor-branded / custom QR</option>
                </select>
              </LabeledField>
              <LabeledField label="Sponsor / organization"><input data-testid="edit-sponsor" value={form.sponsor_name} onChange={(e) => set("sponsor_name", e.target.value)} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" placeholder="e.g. Grace Church" /></LabeledField>
              <LabeledField label="Tracking number"><input data-testid="edit-tracking-code" value={form.tracking_code} onChange={(e) => set("tracking_code", e.target.value)} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" /></LabeledField>
              <LabeledField label="Tracking URL"><input data-testid="edit-tracking-url" value={form.tracking_url} onChange={(e) => set("tracking_url", e.target.value)} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" placeholder="https://…" /></LabeledField>
              <LabeledField label="Reprint / refund status"><input data-testid="edit-reprint" value={form.reprint_refund_status} onChange={(e) => set("reprint_refund_status", e.target.value)} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" placeholder="e.g. Reprint requested" /></LabeledField>
              <LabeledField label="Fulfillment notes"><textarea data-testid="edit-notes" value={form.admin_notes} onChange={(e) => set("admin_notes", e.target.value)} rows={3} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" /></LabeledField>
              <label className="flex items-center gap-2 text-sm text-[#475569]">
                <input data-testid="edit-notify" type="checkbox" checked={form.notify_customer} onChange={(e) => set("notify_customer", e.target.checked)} />
                Email the customer when marking as Shipped {d.shipped_email_sent && <span className="text-xs text-[#166534]">(already sent)</span>}
              </label>
              <button data-testid="order-save-btn" onClick={save} disabled={saving}
                className="w-full rounded-full bg-[#2C4C3B] text-white py-2.5 font-semibold hover:bg-[#24402F] transition-colors disabled:opacity-60">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
function DRow({ label, value }) {
  return <div className="flex justify-between gap-6"><dt className="text-[#94A3B8] shrink-0">{label}</dt><dd className="text-[#0F172A] font-medium text-right break-all">{value}</dd></div>;
}
function LabeledField({ label, children }) {
  return <div><label className="block text-xs uppercase tracking-wide text-[#94A3B8] mb-1">{label}</label>{children}</div>;
}

function CardOrders() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [fStatus, setFStatus] = useState("");
  const [fQty, setFQty] = useState("");
  const [fSponsor, setFSponsor] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const load = () => api.get("/admin/card-orders").then((r) => setData(r.data)).catch(() => toast.error("Could not load orders"));
  useEffect(() => { load(); }, []);
  const onSaved = (updated) => {
    if (updated) setData((d) => d ? { ...d, orders: d.orders.map((o) => o.id === updated.id ? updated : o) } : d);
    load();  // also refresh stats/funnel
  };
  const retry = async (id) => {
    setBusy(id);
    try { const r = await api.post(`/admin/card-orders/${id}/retry-fulfillment`); toast.success(`Retry: ${r.data.status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
    finally { setBusy(""); }
  };
  if (!data) return <p className="text-[#94A3B8]">Loading…</p>;
  const badge = (t) => <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${t ? "bg-[#FEF3C7] text-[#92400E]" : "bg-[#EAF1EA] text-[#2C4C3B]"}`}>{t ? "TEST" : "LIVE"}</span>;
  const orders = data.orders.filter((o) => {
    if (fStatus && o.effective_status !== fStatus) return false;
    if (fQty && String(o.quantity) !== fQty) return false;
    if (fSponsor && !(o.sponsor_name || "").toLowerCase().includes(fSponsor.toLowerCase())) return false;
    const day = (o.created_at || "").slice(0, 10);
    if (fFrom && day && day < fFrom) return false;
    if (fTo && day && day > fTo) return false;
    return true;
  });
  const selCls = "rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm bg-white";
  return (
    <div>
      <CardOrderStats />
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        <span className={`px-3 py-1.5 rounded-full ${data.live_fulfillment ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#EAF1EA] text-[#2C4C3B]"}`}>Live fulfillment: {data.live_fulfillment ? "ON" : "OFF (draft/test only)"}</span>
        <span className={`px-3 py-1.5 rounded-full ${data.product_ready ? "bg-[#EAF1EA] text-[#2C4C3B]" : "bg-[#FEF3C7] text-[#92400E]"}`}>Product UID: {data.product_ready ? "set" : "NOT SET"}</span>
        <span className={`px-3 py-1.5 rounded-full ${data.artwork_ready ? "bg-[#EAF1EA] text-[#2C4C3B]" : "bg-[#FEF3C7] text-[#92400E]"}`}>Artwork URL: {data.artwork_ready ? "set" : "NOT SET"}</span>
      </div>

      {/* Filters */}
      <div data-testid="card-order-filters" className="flex flex-wrap items-end gap-3 mb-4">
        <div><label className="block text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Status</label>
          <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={selCls}>
            <option value="">All statuses</option>
            <option value="awaiting_payment">Awaiting payment</option>
            <option value="new">New</option>
            <option value="in_production">In Production</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select></div>
        <div><label className="block text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Quantity</label>
          <select data-testid="filter-qty" value={fQty} onChange={(e) => setFQty(e.target.value)} className={selCls}>
            <option value="">All</option><option value="100">100</option><option value="200">200</option><option value="500">500</option>
          </select></div>
        <div><label className="block text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">From</label>
          <input data-testid="filter-from" type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className={selCls} /></div>
        <div><label className="block text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">To</label>
          <input data-testid="filter-to" type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className={selCls} /></div>
        <div className="flex-1 min-w-[160px]"><label className="block text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Sponsor</label>
          <input data-testid="filter-sponsor" value={fSponsor} onChange={(e) => setFSponsor(e.target.value)} placeholder="Search organization…" className={`${selCls} w-full`} /></div>
        <button data-testid="filter-clear" onClick={() => { setFStatus(""); setFQty(""); setFSponsor(""); setFFrom(""); setFTo(""); }}
          className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm text-[#475569] hover:border-[#2C4C3B]">Clear</button>
      </div>

      <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Orders ({orders.length}) — click a row to manage</h3>
      {orders.length === 0 ? <p className="text-[#94A3B8]">No orders match these filters.</p> : (
        <div className="overflow-x-auto">
          <table data-testid="admin-card-orders-table" className="w-full text-sm border-collapse">
            <thead><tr className="text-left text-[#94A3B8] border-b border-[#E2E8F0]">
              {["Date","Order #","Customer","Sponsor","Qty","Type","Paid","Payment","Status","Tracking","Mode",""].map((h) => <th key={h} className="py-2 pr-3 font-medium whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`} onClick={() => setDetailId(o.id)}
                  className="border-b border-[#F1F5F9] align-top cursor-pointer hover:bg-[#FAFAF7]">
                  <td className="py-2 pr-3 whitespace-nowrap">{(o.created_at || "").slice(0, 10)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-[#64748B]">{o.order_reference_id}</td>
                  <td className="py-2 pr-3"><div>{o.customer_name}</div><div className="text-[11px] text-[#94A3B8]">{o.customer_email}</div></td>
                  <td className="py-2 pr-3">{o.sponsor_name || "—"}</td>
                  <td className="py-2 pr-3">{o.quantity}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{o.card_type === "sponsor_qr" ? "Sponsor/QR" : "Standard"}</td>
                  <td className="py-2 pr-3">{o.amount_display}</td>
                  <td className="py-2 pr-3">{o.payment_status}</td>
                  <td className="py-2 pr-3"><StatusBadge status={o.effective_status} /></td>
                  <td className="py-2 pr-3">{o.tracking_url ? <a href={o.tracking_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#2C4C3B] underline">{o.tracking_code}</a> : (o.tracking_code || "—")}</td>
                  <td className="py-2 pr-3">{badge(o.test_mode)}</td>
                  <td className="py-2 pr-3">
                    {o.payment_status === "paid" && !o.gelato_order_id && (
                      <button data-testid={`retry-${o.id}`} onClick={(e) => { e.stopPropagation(); retry(o.id); }} disabled={busy === o.id}
                        className="text-xs rounded-full border border-[#2C4C3B] text-[#2C4C3B] px-3 py-1 hover:bg-[#F1F5F1] disabled:opacity-50">
                        {busy === o.id ? "…" : "Retry"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detailId && <OrderDetailModal id={detailId} onClose={() => setDetailId(null)} onSaved={onSaved} />}
    </div>
  );
}

function Verses() {
  const [verses, setVerses] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const load = () => api.get("/admin/verses", { params: { q: q || undefined, status: status || undefined } }).then((r) => setVerses(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search reference, word, keyword…" className="flex-1 min-w-[220px] rounded-full border border-[#E2E8F0] px-4 py-2.5 focus:outline-none focus:border-[#2C4C3B]" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-[#E2E8F0] px-4 py-2.5 bg-white">
          <option value="">All statuses</option><option value="approved">Approved</option><option value="pending">Pending</option>
        </select>
        <button data-testid="admin-search-btn" onClick={load} className="px-5 py-2.5 rounded-full bg-[#2C4C3B] text-white font-medium">Search</button>
      </div>
      <p className="text-sm text-[#94A3B8] mb-4">{verses.length} verses</p>
      <div className="space-y-4">{verses.map((v) => <VerseEditor key={v.id} v={v} onSaved={load} />)}</div>
    </div>
  );
}

function VerseEditor({ v, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ explanation: v.explanation || "", application: v.application || "", prayer: v.prayer || "",
    issues: (v.issues || []).join(", "), keywords: (v.keywords || []).join(", "), related: (v.related || []).join(", ") });
  const save = async () => {
    try {
      await api.put(`/admin/verses/${v.id}`, { explanation: f.explanation, application: f.application, prayer: f.prayer,
        issues: f.issues.split(",").map((s) => s.trim()).filter(Boolean),
        keywords: f.keywords.split(",").map((s) => s.trim()).filter(Boolean),
        related: f.related.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success(`${v.reference} updated.`); onSaved();
    } catch { toast.error("Could not save."); }
  };
  const review = async () => { await api.post(`/admin/verses/${v.id}/review`); toast.success("Marked reviewed & approved."); onSaved(); };
  return (
    <div data-testid={`admin-verse-${v.id}`} className="rounded-xl border border-[#E2E8F0] bg-white">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left">
        <div>
          <span className="font-semibold text-[#2C4C3B]">{v.reference}</span>
          <span className="text-sm text-[#475569] ml-3">{v.text.slice(0, 70)}…</span>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ${v.review_status === "approved" ? "bg-[#F1F5F1] text-[#2C4C3B]" : "bg-[#F9F0DC] text-[#B8912F]"}`}>{v.review_status}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t border-[#E2E8F0] pt-4">
          <Field label="Explanation" value={f.explanation} onChange={(x) => setF({ ...f, explanation: x })} area />
          <Field label="Practical application" value={f.application} onChange={(x) => setF({ ...f, application: x })} area />
          <Field label="Suggested prayer" value={f.prayer} onChange={(x) => setF({ ...f, prayer: x })} area />
          <Field label="Issue tags (comma-separated)" value={f.issues} onChange={(x) => setF({ ...f, issues: x })} />
          <Field label="Keywords" value={f.keywords} onChange={(x) => setF({ ...f, keywords: x })} />
          <Field label="Related verses" value={f.related} onChange={(x) => setF({ ...f, related: x })} />
          <div className="flex gap-2 pt-1">
            <button data-testid={`admin-save-${v.id}`} onClick={save} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-[#2C4C3B] text-white text-sm font-medium"><Save className="w-4 h-4" /> Save</button>
            <button data-testid={`admin-review-${v.id}`} onClick={review} className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-[#2C4C3B] text-[#2C4C3B] text-sm font-medium"><Check className="w-4 h-4" /> Mark reviewed</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, area }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#94A3B8] font-semibold mb-1">{label}</label>
      {area ? <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#2C4C3B]" />
        : <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm focus:outline-none focus:border-[#2C4C3B]" />}
    </div>
  );
}

function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setA(r.data)); }, []);
  if (!a) return null;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Users" value={a.total_users} /><Stat label="Verses" value={a.total_verses} />
        <Stat label="Pending review" value={a.pending_review} /><Stat label="Daily subscribers" value={a.daily_subscribers} />
      </div>

      {a.growth && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Sharing & growth</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat label="Total searches" value={a.growth.total_searches} />
            <Stat label="Unique visitors" value={a.growth.unique_visitors} />
            <Stat label="Shares initiated" value={a.growth.shares_initiated} />
            <Stat label="Links copied" value={a.growth.links_copied} />
            <Stat label="Visits from shares" value={a.growth.shared_visits} />
            <Stat label="Accounts from shares" value={a.growth.accounts_from_shares} />
            <Stat label="LifeGuide clicks" value={a.growth.lifeguide_clicks} />
            <Stat label="LifeGuide from shared" value={a.growth.lifeguide_clicks_from_shared} />
          </div>
        </div>
      )}

      {a.anonymous && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Anonymous sharing</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Stat label="Anon shares created" value={a.anonymous.anon_shares_created} />
            <Stat label="Anon links opened" value={a.anonymous.anon_links_opened} />
            <Stat label="Recipient searches" value={a.anonymous.anon_recipient_searches} />
            <Stat label="Recipient accounts" value={a.anonymous.anon_recipient_accounts} />
            <Stat label="LifeGuide conversions" value={a.anonymous.anon_lifeguide_conversions} />
          </div>
        </div>
      )}

      {a.pwa && (
        <div data-testid="pwa-analytics">
          <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Save to Phone (PWA)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Stat label="Prompt shown" value={a.pwa.prompt_shown} />
            <Stat label="Save clicked" value={a.pwa.save_clicked} />
            <Stat label="Install accepted" value={a.pwa.install_accepted} />
            <Stat label="Install dismissed" value={a.pwa.install_dismissed} />
            <Stat label="iPhone steps viewed" value={a.pwa.ios_instructions_viewed} />
          </div>
        </div>
      )}

      {a.top_shared_issues?.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Top shared issues</h3>
          <div className="flex flex-wrap gap-2">
            {a.top_shared_issues.map((s) => <span key={s.slug} className="px-3 py-1.5 rounded-full bg-[#F1F5F1] text-sm text-[#2C4C3B]">{s.label} · {s.count}</span>)}
          </div>
        </div>
      )}

      {a.top_shared_results?.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Top shared Proverbs</h3>
          <div className="flex flex-wrap gap-2">
            {a.top_shared_results.map((s) => <span key={s.reference} className="px-3 py-1.5 rounded-full bg-[#F9F7F1] text-sm text-[#475569]">{s.reference} · {s.count}</span>)}
          </div>
        </div>
      )}
      <div>
        <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Most selected struggles</h3>
        {a.top_struggles.length === 0 ? <p className="text-sm text-[#94A3B8]">No data yet.</p> :
          <div className="space-y-2">{a.top_struggles.map((s) => (
            <div key={s.slug} className="flex items-center gap-3">
              <span className="w-40 text-sm text-[#475569]">{s.label}</span>
              <div className="flex-1 h-2.5 bg-[#F4F1EA] rounded-full overflow-hidden"><div className="h-full bg-[#2C4C3B]" style={{ width: `${Math.min(100, s.count * 12)}%` }} /></div>
              <span className="text-sm text-[#94A3B8] w-8 text-right">{s.count}</span>
            </div>))}</div>}
      </div>
      <div>
        <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Searches with weak / no results</h3>
        {a.weak_searches.length === 0 ? <p className="text-sm text-[#94A3B8]">None recorded.</p> :
          <ul className="space-y-1.5">{a.weak_searches.map((w, i) => <li key={i} className="text-sm text-[#475569]">{w.issues.join(", ")} {w.situation && `— "${w.situation.slice(0, 60)}"`}</li>)}</ul>}
      </div>
    </div>
  );
}
function Stat({ label, value }) {
  return <div className="rounded-xl border border-[#E2E8F0] bg-white p-5"><p className="text-3xl font-semibold text-[#0F172A]">{value}</p><p className="text-xs uppercase tracking-widest text-[#94A3B8] mt-1">{label}</p></div>;
}

function ContentTools() {
  const [topics, setTopics] = useState([]);
  const [genSrc, setGenSrc] = useState("wisdom");
  const [genMsg, setGenMsg] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [lib, setLib] = useState(null);
  const loadTopics = () => api.get("/admin/topics").then((r) => setTopics(r.data)).catch(() => {});
  useEffect(() => { loadTopics(); api.get("/site-config").then((r) => setCfg(r.data)).catch(() => {}); api.get("/library-stats").then((r) => setLib(r.data)).catch(() => {}); }, []);

  const saveTopic = async (t) => {
    try {
      await api.post("/admin/topics", { slug: t.slug, label: t.label, group: t.group,
        subtopics: typeof t.subtopics === "string" ? t.subtopics.split(",").map((s) => s.trim()).filter(Boolean) : t.subtopics,
        order: t.order, active: t.active });
      toast.success(`${t.label} saved.`); loadTopics();
    } catch { toast.error("Could not save topic."); }
  };
  const generate = async () => {
    setGenBusy(true); setGenMsg("");
    try {
      const { data } = await api.post("/admin/generate-explanations", null, { params: { source: genSrc, limit: 15 } });
      setGenMsg(`Generated ${data.generated}. ${data.remaining_without_explanation} still missing an explanation.`);
      toast.success(`Generated ${data.generated} explanations.`);
    } catch { toast.error("Generation failed."); } finally { setGenBusy(false); }
  };
  const saveCfg = async () => {
    try { await api.put("/admin/site-config", { config: cfg }); toast.success("Bridge copy saved."); }
    catch { toast.error("Could not save."); }
  };

  return (
    <div className="space-y-10">
      {lib && (
        <div data-testid="admin-library-stats" className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="font-serif-display text-2xl text-[#0F172A] mb-4">Scripture library (live, approved only)</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Stat label="Proverbs" value={lib.proverbs} />
            <Stat label="Psalm verses" value={lib.psalms} />
            <Stat label="Psalms" value={lib.psalm_chapters} />
            <Stat label="More Wisdom" value={lib.wisdom} />
            <Stat label="Total passages" value={lib.total} />
            <Stat label="Input combos" value={(lib.combinations || 0).toLocaleString()} />
          </div>
          <p className="text-xs text-[#94A3B8] mt-3">Counts are distinct approved references (no duplicates). "Input combos" = defensible number of personalization selections (concerns×needs×sources), not unique verses.</p>
        </div>
      )}

      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <h3 className="font-serif-display text-2xl text-[#0F172A] mb-2">Generate short explanations</h3>
        <p className="text-sm text-[#475569] mb-4">Pre-generate the short 1–2 sentence "why this may relate" text (AI) for topic-mapped verses that don't have one yet. These appear as the explanation and are editable per verse in the Verses tab. They are never presented as Scripture.</p>
        <div className="flex flex-wrap items-center gap-2">
          <select value={genSrc} onChange={(e) => setGenSrc(e.target.value)} className="rounded-full border border-[#E2E8F0] px-4 py-2.5 bg-white">
            <option value="wisdom">More Biblical Wisdom</option><option value="psalms">Psalms</option><option value="proverbs">Proverbs</option>
          </select>
          <button data-testid="admin-generate-explanations" onClick={generate} disabled={genBusy} className="px-5 py-2.5 rounded-full bg-[#2C4C3B] text-white font-medium disabled:opacity-60">{genBusy ? "Generating…" : "Generate (batch of 15)"}</button>
        </div>
        {genMsg && <p className="text-sm text-[#2C4C3B] mt-3">{genMsg}</p>}
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-widest text-[#B8912F] font-semibold mb-3">Topics & subtopics ({topics.length})</h3>
        <div className="space-y-3">
          {topics.map((t, i) => (
            <div key={t.slug} data-testid={`admin-topic-${t.slug}`} className="rounded-xl border border-[#E2E8F0] bg-white p-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label={`Label (${t.slug})`} value={t.label} onChange={(x) => { const c = [...topics]; c[i] = { ...t, label: x }; setTopics(c); }} />
                <Field label="Group" value={t.group} onChange={(x) => { const c = [...topics]; c[i] = { ...t, group: x }; setTopics(c); }} />
              </div>
              <div className="mt-3"><Field label="Subtopics (comma-separated)" value={Array.isArray(t.subtopics) ? t.subtopics.join(", ") : t.subtopics} onChange={(x) => { const c = [...topics]; c[i] = { ...t, subtopics: x }; setTopics(c); }} /></div>
              <div className="flex items-center gap-3 mt-3">
                <label className="text-sm text-[#475569] flex items-center gap-1.5"><input type="checkbox" checked={t.active} onChange={(e) => { const c = [...topics]; c[i] = { ...t, active: e.target.checked }; setTopics(c); }} /> Active</label>
                <button data-testid={`admin-save-topic-${t.slug}`} onClick={() => saveTopic(topics[i])} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2C4C3B] text-white text-sm font-medium"><Save className="w-4 h-4" /> Save</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {cfg && (
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="font-serif-display text-2xl text-[#0F172A] mb-3">LifeGuide bridge copy</h3>
          <div className="space-y-3">
            <Field label="Bridge headline" value={cfg.bridge_headline || ""} onChange={(x) => setCfg({ ...cfg, bridge_headline: x })} />
            <Field label="Bridge subtext" value={cfg.bridge_sub || ""} onChange={(x) => setCfg({ ...cfg, bridge_sub: x })} area />
            <Field label="Bridge points (comma-separated)" value={(cfg.bridge_points || []).join(", ")} onChange={(x) => setCfg({ ...cfg, bridge_points: x.split(",").map((s) => s.trim()).filter(Boolean) })} />
            <Field label="Bridge button" value={cfg.bridge_button || ""} onChange={(x) => setCfg({ ...cfg, bridge_button: x })} />
          </div>
          <button data-testid="admin-save-config" onClick={saveCfg} className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#2C4C3B] text-white font-medium"><Save className="w-4 h-4" /> Save bridge copy</button>
        </div>
      )}
    </div>
  );
}


function DataTools() {
  const fileRef = useRef();
  const [result, setResult] = useState("");
  const importCsv = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } }); setResult(`Imported ${data.imported} verses.`); toast.success(`Imported ${data.imported} verses.`); }
    catch { toast.error("Import failed. Check CSV format."); }
  };
  const exportCsv = async () => {
    const res = await api.get("/admin/export-csv", { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "proverbs-export.csv"; a.click(); URL.revokeObjectURL(url);
  };
  return (
    <div className="grid sm:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <h3 className="font-serif-display text-2xl text-[#0F172A] mb-2">Import Scripture (CSV)</h3>
        <p className="text-sm text-[#475569] mb-4">Columns: reference, chapter, verse, text, translation, primary_topic, issues, keywords, explanation, application, prayer, related, review_status. Use <code>;</code> to separate list values.</p>
        <input ref={fileRef} data-testid="import-csv-input" type="file" accept=".csv" onChange={importCsv} className="hidden" />
        <button data-testid="import-csv-btn" onClick={() => fileRef.current.click()} className="px-5 py-2.5 rounded-full bg-[#2C4C3B] text-white font-medium">Choose CSV file</button>
        {result && <p className="text-sm text-[#2C4C3B] mt-3">{result}</p>}
      </div>
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
        <h3 className="font-serif-display text-2xl text-[#0F172A] mb-2">Export database</h3>
        <p className="text-sm text-[#475569] mb-4">Download the complete Proverbs database as a CSV file.</p>
        <button data-testid="export-csv-btn" onClick={exportCsv} className="px-5 py-2.5 rounded-full border border-[#2C4C3B] text-[#2C4C3B] font-medium">Export CSV</button>
      </div>
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)); }, []);
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#F4F1EA] text-[#475569]"><tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Plan</Th></tr></thead>
        <tbody>{users.map((u) => (
          <tr key={u.id} className="border-t border-[#E2E8F0]"><Td>{u.name}</Td><Td>{u.email}</Td><Td className="capitalize">{u.role}</Td><Td className="capitalize">{u.tier}</Td></tr>))}</tbody>
      </table>
    </div>
  );
}
const Th = ({ children }) => <th className="text-left px-4 py-3 font-semibold">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-[#475569] ${className}`}>{children}</td>;
